import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const POSTCODE = /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i;

// "269 Great Cheetham Street East, Salford, M7 4UF" -> first line / second line / postcode
export function splitAddress(address = "") {
  let rest = address.trim().replace(/,\s*(UK|United Kingdom)$/i, "");
  let postcode = "";
  const m = rest.match(POSTCODE);
  if (m) {
    postcode = m[1].toUpperCase();
    rest = rest.slice(0, m.index).replace(/[,\s]+$/, "");
  }
  const parts = rest.split(",").map((s) => s.trim()).filter(Boolean);
  return { line1: parts[0] || "", line2: parts.slice(1).join(", "), postcode };
}

export const joinAddress = ({ line1, line2, postcode }) => [line1, line2, postcode].map((s) => s.trim()).filter(Boolean).join(", ");

export function PropertyAddressLines({ property, onSaved }) {
  const [f, setF] = useState(splitAddress(property.address));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(splitAddress(property.address)); }, [property.id, property.address]);
  const changed = joinAddress(f) !== joinAddress(splitAddress(property.address));
  const save = async () => {
    if (!f.line1.trim()) { toast.error("First line is required"); return; }
    setBusy(true);
    try {
      const address = joinAddress(f);
      await client.patch(`/properties/${property.id}`, { clientId: property.clientId, address });
      toast.success("Address updated for this property");
      onSaved(address);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const row = (key, label, testid, cls = "") => (
    <div className={cls}>
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <Input value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={testid} />
    </div>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3" data-testid="event-address-lines">
      {row("line1", "First line", "event-address-line1")}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-3">
        {row("line2", "Second line (town / city)", "event-address-line2")}
        {row("postcode", "Postcode", "event-address-postcode")}
      </div>
      {changed && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-amber-700">Saving changes this property's address for all its jobs.</p>
          <Button type="button" onClick={save} disabled={busy} data-testid="event-address-save" className="bg-brand hover:bg-brand-dark shrink-0">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save address
          </Button>
        </div>
      )}
    </div>
  );
}
